import { Fragment } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, CaretRight } from '@phosphor-icons/react';
import { useTheme } from '@emotion/react';

import { Link } from 'react-router-dom';
import { IconButton } from 'components/button/IconButton';
import { Text } from 'components/text/Text';

export type BreadcrumbItem = {
  lightText?: string;
  text?: string;
  link?: string;
  onClick?: () => void;
};

type BreadcrumbsProps = {
  crumbs: BreadcrumbItem[];
  onBackClick?: () => void;
  big?: boolean;
};

/**
 * Breadcrumbs component, part of Delogue design system, for displaying navigation hierarchy.
 *
 * @param crumbs - Array of breadcrumb items, each can have lightText (disabled color) and/or text (default color)
 * @param onBackClick - Optional click handler for the back arrow button
 * @param big - Optional boolean to make the breadcrumbs bigger
 *
 * @example
 * ```tsx
 * <Breadcrumbs
 *   crumbs={[
 *     { lightText: 'Home' },
 *     { lightText: 'Products' },
 *     { text: 'Item Details' }
 *   ]}
 *   onBackClick={() => navigate(-1)}
 * />
 * ```
 */
export const Breadcrumbs = ({ crumbs, onBackClick, big }: BreadcrumbsProps) => {
  const theme = useTheme();
  const arrowSize = big ? 32 : 20;
  const textVariant = big ? 'headlineSm' : 'bodySm';
  const textWeight = big ? 'semiBold' : undefined;

  return (
    <BreadcrumbsWrapper big={big}>
      {onBackClick && (
        <IconButton size={big ? 'lg' : 'sm'} onClick={onBackClick} data-size={big ? 'lg' : 'sm'}>
          <ArrowLeft
            data-testid="arrow_left"
            data-size={arrowSize}
            size={arrowSize}
            style={{ minWidth: `${arrowSize}px` }}
            color={theme.palette.fg.primary}
          />
        </IconButton>
      )}
      <BreadcrumbsContainer big={big} data-testid="breadcrumb-title">
        {crumbs.map((crumb, index) => {
          const key = `${crumb.lightText || ''}-${crumb.text || ''}`;
          const renderText = (text: string, isLight?: boolean) => {
            const textElement = (
              <Text
                variant={textVariant}
                weight={textWeight}
                color={isLight ? 'disabled' : undefined}
              >
                {text}
              </Text>
            );
            if (crumb.link) {
              return (
                <StyledLink to={crumb.link} onClick={crumb.onClick}>
                  {textElement}
                </StyledLink>
              );
            }
            if (crumb.onClick) {
              return <ClickableText onClick={crumb.onClick}>{textElement}</ClickableText>;
            }
            return textElement;
          };
          return (
            <Fragment key={key}>
              {crumb.lightText && renderText(crumb.lightText, true)}
              {crumb.text && renderText(crumb.text)}
              {index < crumbs.length - 1 && (
                <CaretRight
                  data-testid="caret_right"
                  size={big ? 20 : 14}
                  color={theme.palette.fg.secondary}
                />
              )}
            </Fragment>
          );
        })}
      </BreadcrumbsContainer>
    </BreadcrumbsWrapper>
  );
};

const StyledLink = styled(Link)`
  cursor: pointer;
  :hover {
    > * {
      color: ${props => props.theme.palette.text.primary};
    }
  }
`;

const ClickableText = styled.span`
  cursor: pointer;
  :hover {
    > * {
      color: ${props => props.theme.palette.text.primary};
    }
  }
`;

const BreadcrumbsWrapper = styled.div<{ big?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => (props.big ? '24px' : '4px')};
  flex: 1;
  justify-content: flex-start;
`;

const BreadcrumbsContainer = styled.div<{ big?: boolean }>`
  display: flex;
  gap: 8px;
  height: ${props => (props.big ? '32px' : '20px')};
  align-items: center;
  svg {
    padding-left: 1px;
    padding-right: 1px;
  }
`;
